import { useState } from 'react';
import {
	Button,
	Card,
	Col,
	Empty,
	Form,
	Input,
	InputNumber,
	Modal,
	Row,
	Select,
	Space,
	Table,
	Tag,
	Tooltip,
	Typography,
	message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { handleAPIError } from '../../utils/helpers';

interface Shop {
	id: number;
	name: string;
	latitude: string;
	longitude: string;
	checkin_radius_m: number;
	work_start: string;
	work_end: string;
	is_active?: boolean;
	supervisor_msisdn?: string | null;
	agents?: number;
}

interface Agent {
	id: number;
	full_name: string;
	phone_number: string;
	status: 'active' | 'suspended';
	shop: Shop | null;
	pin?: string;
}

export default function FieldAgents() {
	const queryClient = useQueryClient();

	const [shopForm] = Form.useForm();
	const [agentForm] = Form.useForm();
	const [editingShop, setEditingShop] = useState<Shop | null>(null);
	const [shopModal, setShopModal] = useState(false);
	const [agentModal, setAgentModal] = useState(false);
	// Shown once, then never again — the whole point of the dialog.
	const [issuedPin, setIssuedPin] = useState<{ name: string; phone: string; pin: string } | null>(
		null,
	);

	const { data: shops = [], isLoading: shopsLoading, error: shopsError } = useQuery<Shop[]>({
		queryKey: ['field-shops'],
		queryFn: async () => {
			const res = await axios.get('/admin/field/shops', { withCredentials: true });
			return res.data.results;
		},
	});
	if (shopsError) handleAPIError(shopsError);

	const { data: agents = [], isLoading: agentsLoading, error: agentsError } = useQuery<Agent[]>({
		queryKey: ['field-agents'],
		queryFn: async () => {
			const res = await axios.get('/admin/field/agents', { withCredentials: true });
			return res.data.results;
		},
	});
	if (agentsError) handleAPIError(agentsError);

	const saveShop = useMutation({
		mutationFn: async (values: Partial<Shop>) => {
			if (editingShop) {
				await axios.patch(`/admin/field/shops/${editingShop.id}`, values, {
					withCredentials: true,
				});
			} else {
				await axios.post('/admin/field/shops', values, { withCredentials: true });
			}
		},
		onSuccess: () => {
			message.success(editingShop ? 'Office updated' : 'Office added');
			queryClient.invalidateQueries({ queryKey: ['field-shops'] });
			setShopModal(false);
			setEditingShop(null);
			shopForm.resetFields();
		},
		onError: handleAPIError,
	});

	const createAgent = useMutation({
		mutationFn: async (values: Record<string, unknown>) => {
			const res = await axios.post('/admin/field/agents', values, { withCredentials: true });
			return res.data.results as Agent;
		},
		// The server refuses an existing account but says when it can be turned
		// into an agent, so offer that rather than making the admin retype it.
		onError: (error: Error) => {
			const msg =
				(error as unknown as { response?: { data?: { msg?: string } } })?.response?.data
					?.msg ?? '';
			if (msg.includes('can be made an agent')) {
				const values = agentForm.getFieldsValue();
				Modal.confirm({
					title: 'Use the existing account?',
					content: msg.replace(' Retry with convert_existing to do it.', ''),
					okText: 'Make them an agent',
					onOk: () => createAgent.mutateAsync({ ...values, convert_existing: true }),
				});
				return;
			}
			handleAPIError(error);
		},
		onSuccess: (agent) => {
			queryClient.invalidateQueries({ queryKey: ['field-agents'] });
			queryClient.invalidateQueries({ queryKey: ['field-shops'] });
			setAgentModal(false);
			agentForm.resetFields();
			if (agent.pin) {
				setIssuedPin({
					name: agent.full_name,
					phone: agent.phone_number,
					pin: agent.pin,
				});
			}
		},
	});

	const updateAgent = useMutation({
		mutationFn: async (payload: {
			id: number;
			shop_id?: number;
			status?: string;
			reset_pin?: boolean;
		}) => {
			const res = await axios.patch(`/admin/field/agents/${payload.id}`, payload, {
				withCredentials: true,
			});
			return res.data.results as Agent;
		},
		onSuccess: (agent, variables) => {
			queryClient.invalidateQueries({ queryKey: ['field-agents'] });
			if (variables.reset_pin && agent.pin) {
				setIssuedPin({
					name: agent.full_name,
					phone: agent.phone_number,
					pin: agent.pin,
				});
			} else {
				message.success('Agent updated');
			}
		},
		onError: handleAPIError,
	});

	const openShop = (shop: Shop | null) => {
		setEditingShop(shop);
		shopForm.setFieldsValue(
			shop ?? { checkin_radius_m: 100, work_start: '10:00', work_end: '19:00' },
		);
		setShopModal(true);
	};

	const confirmSuspend = (agent: Agent) => {
		const suspending = agent.status === 'active';
		Modal.confirm({
			title: suspending ? `Suspend ${agent.full_name}?` : `Reactivate ${agent.full_name}?`,
			content: suspending
				? 'They lose access to the app on their next request. Their account stays intact and you can reactivate at any time.'
				: 'They get access back immediately.',
			okText: suspending ? 'Suspend' : 'Reactivate',
			okButtonProps: { danger: suspending },
			onOk: () =>
				updateAgent.mutateAsync({
					id: agent.id,
					status: suspending ? 'suspended' : 'active',
				}),
		});
	};

	const shopColumns = [
		{
			title: 'Office',
			key: 'name',
			render: (_: unknown, r: Shop) => (
				<Space direction="vertical" size={0}>
					<span style={{ fontWeight: 500 }}>{r.name}</span>
					<span style={{ fontSize: 12, color: '#888' }}>
						{r.work_start}–{r.work_end} · {r.checkin_radius_m}m radius
					</span>
				</Space>
			),
		},
		{
			title: 'Coordinates',
			key: 'coords',
			render: (_: unknown, r: Shop) => (
				<a
					href={`https://maps.google.com/?q=${r.latitude},${r.longitude}`}
					target="_blank"
					rel="noreferrer"
				>
					{r.latitude}, {r.longitude}
				</a>
			),
		},
		{
			title: 'Supervisor',
			key: 'supervisor',
			render: (_: unknown, r: Shop) =>
				r.supervisor_msisdn || (
					<Tooltip title="No escalation number">
						<Tag color="orange">Not set</Tag>
					</Tooltip>
				),
		},
		{ title: 'Agents', dataIndex: 'agents', key: 'agents' },
		{
			title: '',
			key: 'action',
			render: (_: unknown, r: Shop) => (
				<Button size="small" onClick={() => openShop(r)}>
					Edit
				</Button>
			),
		},
	];

	const agentColumns = [
		{
			title: 'Agent',
			key: 'name',
			render: (_: unknown, r: Agent) => (
				<Space direction="vertical" size={0}>
					<span style={{ fontWeight: 500 }}>{r.full_name}</span>
					<span style={{ fontSize: 12, color: '#888' }}>{r.phone_number}</span>
				</Space>
			),
		},
		{
			title: 'Office',
			key: 'shop',
			render: (_: unknown, r: Agent) => r.shop?.name ?? <Tag color="orange">None</Tag>,
		},
		{
			title: 'Status',
			key: 'status',
			render: (_: unknown, r: Agent) => (
				<Tag color={r.status === 'active' ? 'green' : 'red'}>{r.status.toUpperCase()}</Tag>
			),
		},
		{
			title: 'Action',
			key: 'action',
			render: (_: unknown, r: Agent) => (
				<Space>
					<Select
						size="small"
						style={{ width: 150 }}
						value={r.shop?.id}
						placeholder="Assign office"
						options={shops.map((s) => ({ value: s.id, label: s.name }))}
						onChange={(shop_id) => updateAgent.mutate({ id: r.id, shop_id })}
					/>
					<Button
						size="small"
						onClick={() => updateAgent.mutate({ id: r.id, reset_pin: true })}
					>
						New PIN
					</Button>
					<Button
						size="small"
						danger={r.status === 'active'}
						onClick={() => confirmSuspend(r)}
					>
						{r.status === 'active' ? 'Suspend' : 'Reactivate'}
					</Button>
				</Space>
			),
		},
	];

	return (
		<AppLayout title="Field Sales Team" subtitle="Zazu premises agents report to, and the agents themselves">
			<div style={{ padding: 16 }}>
				<Row gutter={[16, 16]}>
					<Col span={24}>
						<Card
							size="small"
							title="Zazu Offices"
							extra={
								<Button type="primary" size="small" onClick={() => openShop(null)}>
									Add office
								</Button>
							}
						>
							<Table
								rowKey="id"
								size="small"
								loading={shopsLoading}
								dataSource={shops}
								columns={shopColumns}
								pagination={false}
								locale={{ emptyText: <Empty description="No office yet — add one before creating agents" /> }}
							/>
						</Card>
					</Col>

					<Col span={24}>
						<Card
							size="small"
							title="Agents"
							extra={
								<Tooltip title={shops.length ? '' : 'Add an office first'}>
									<Button
										type="primary"
										size="small"
										disabled={!shops.length}
										onClick={() => {
											agentForm.resetFields();
											setAgentModal(true);
										}}
									>
										Add agent
									</Button>
								</Tooltip>
							}
						>
							<Table
								rowKey="id"
								size="small"
								loading={agentsLoading}
								dataSource={agents}
								columns={agentColumns}
								pagination={false}
								locale={{ emptyText: <Empty description="No agents yet" /> }}
							/>
						</Card>
					</Col>
				</Row>
			</div>

			{/* --- shop form --- */}
			<Modal
				open={shopModal}
				title={editingShop ? `Edit ${editingShop.name}` : 'Add Zazu office'}
				okText="Save"
				confirmLoading={saveShop.isPending}
				onCancel={() => {
					setShopModal(false);
					setEditingShop(null);
				}}
				onOk={() => shopForm.submit()}
			>
				<Form form={shopForm} layout="vertical" onFinish={(v) => saveShop.mutate(v)}>
					<Form.Item name="name" label="Name" rules={[{ required: true }]}>
						<Input placeholder="Zazu Kampala Central" />
					</Form.Item>
					<Row gutter={12}>
						<Col span={12}>
							<Form.Item
								name="latitude"
								label="Latitude"
								rules={[{ required: true, message: 'Required — the geofence needs it' }]}
							>
								<Input placeholder="0.313611" />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name="longitude"
								label="Longitude"
								rules={[{ required: true, message: 'Required — the geofence needs it' }]}
							>
								<Input placeholder="32.581111" />
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item name="checkin_radius_m" label="Check-in radius (m)">
								<InputNumber style={{ width: '100%' }} min={20} step={10} />
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item name="work_start" label="Day starts">
								<Input placeholder="10:00" />
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item name="work_end" label="Day ends">
								<Input placeholder="19:00" />
							</Form.Item>
						</Col>
					</Row>
					<Form.Item
						name="supervisor_msisdn"
						label="Supervisor phone"
						tooltip="Escalation number"
					>
						<Input placeholder="2567XXXXXXXX" />
					</Form.Item>
				</Form>
			</Modal>

			{/* --- agent form --- */}
			<Modal
				open={agentModal}
				title="Add agent"
				okText="Create agent"
				confirmLoading={createAgent.isPending}
				onCancel={() => setAgentModal(false)}
				onOk={() => agentForm.submit()}
			>
				<Form form={agentForm} layout="vertical" onFinish={(v) => createAgent.mutate(v)}>
					<Row gutter={12}>
						<Col span={12}>
							<Form.Item name="first_name" label="First name" rules={[{ required: true }]}>
								<Input />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item name="last_name" label="Last name" rules={[{ required: true }]}>
								<Input />
							</Form.Item>
						</Col>
					</Row>
					<Form.Item
						name="phone_number"
						label="Phone number"
						rules={[{ required: true }]}
						tooltip="This is their username in the app"
					>
						<Input placeholder="2567XXXXXXXX" />
					</Form.Item>
					<Form.Item name="shop_id" label="Office" rules={[{ required: true }]}>
						<Select options={shops.map((s) => ({ value: s.id, label: s.name }))} />
					</Form.Item>
					<Form.Item
						name="pin"
						label="PIN"
						tooltip="Blank = generated"
					>
						<Input placeholder="4 digits" maxLength={4} />
					</Form.Item>
				</Form>
			</Modal>

			{/* --- the one time the PIN is visible --- */}
			<Modal
				open={!!issuedPin}
				title="Login details — PIN shown once"
				onCancel={() => setIssuedPin(null)}
				footer={[
					<Button key="done" type="primary" onClick={() => setIssuedPin(null)}>
						Done
					</Button>,
				]}
			>
				{issuedPin && (
					<Space direction="vertical" size={12} style={{ width: '100%' }}>
						<Card size="small">
							<Space direction="vertical" size={4}>
								<span style={{ color: '#888', fontSize: 12 }}>{issuedPin.name}</span>
								<Typography.Text copyable style={{ fontSize: 16 }}>
									{issuedPin.phone}
								</Typography.Text>
								<Typography.Text
									copyable
									strong
									style={{ fontSize: 30, letterSpacing: 6 }}
								>
									{issuedPin.pin}
								</Typography.Text>
							</Space>
						</Card>
					</Space>
				)}
			</Modal>
		</AppLayout>
	);
}
